/**
 * Typed REST API client (task 20.3).
 *
 * Thin wrapper over `fetch` that attaches the Cognito id-token as a bearer
 * (`Authorization: Bearer <jwt>`) to every protected call — the ComputeStack
 * REST API is behind a Cognito authorizer (R24.1). Response types reuse
 * `@rufus/shared-types` where a canonical type already exists (CharacterLedger,
 * CampaignState) rather than redefining them.
 *
 * `fetch` and the token provider are injected so the client is unit-testable
 * with a mocked fetch and no real backend.
 *
 * Endpoint paths mirror the Phase-1 REST handlers:
 *   - character creation / roster (task 18.1, packages/lambda/src/character-creation)
 *   - campaign creation / list (task 18.2, packages/lambda/src/campaign-creation)
 *   - bring-in (task 18.5, packages/lambda/src/bring-in)
 *   - Green Room actions (task 19, packages/lambda/src/green-room)
 *   - the player dice endpoint (task 20.2, packages/lambda/src/dice)
 */

import type {
  CharacterLedger,
  CampaignState,
  CreateCharacterWireRequest,
  CreateCharacterResponse,
  CreateCampaignWireRequest,
  CreateCampaignResponse,
  ListCharactersResponse,
  GetCharacterResponse,
  ListCampaignsResponse,
  GetJournalResponse,
  GetSpatialMapResponse,
  BringInWireRequest,
  BringInResponse,
  GreenRoomWireRequest,
  GreenRoomResponse,
  DiceRollWireRequest,
  DiceRollResponse,
  SubmitTurnWireRequest,
  SubmitTurnResponse,
  CreateIssueWireRequest,
  CreateIssueResponse,
} from "@rufus/shared-types";

// Re-export the shared wire DTOs under the names the SPA screens already import
// from this module, so the contract lives in @rufus/shared-types (single source
// of truth) while callers keep their existing imports. These REPLACE the
// previously hand-maintained local copies that drifted from the server.
export type {
  CreateCharacterWireRequest as CreateCharacterRequest,
  CreateCampaignWireRequest as CreateCampaignRequest,
  BringInWireRequest as BringInRequest,
  GreenRoomWireRequest as GreenRoomRequest,
  GreenRoomAction,
  DiceRollWireRequest as DiceRollRequest,
  SubmitTurnWireRequest as SubmitTurnRequest,
  BringInResponse,
  GreenRoomResponse,
  DiceRollResponse,
  SubmitTurnResponse,
} from "@rufus/shared-types";

/** A function returning the current bearer token (id token) or null. */
export type TokenProvider = () => string | null;

export interface ApiClientOptions {
  readonly baseUrl: string;
  readonly getToken?: TokenProvider;
  readonly fetchImpl?: typeof fetch;
  /**
   * OPTIONAL hook invoked ONCE when a request comes back 401 OR 403
   * (expired/invalid token). It should attempt a token refresh and resolve
   * `true` if a fresh token is now available (the request is then retried once)
   * or `false` if not (the status is surfaced). Wired to the auth facade's
   * `refresh()` so an expired id token is transparently renewed instead of
   * dead-ending API calls. BOTH statuses are covered because the API Gateway
   * Cognito authorizer rejects an expired/invalid token with 403 (not 401) — the
   * request never reaches the handler. The retry is capped at one, so a genuine
   * authorization 403 (e.g. a non-member) does not loop: the refreshed token
   * hits the same 403 and it is surfaced. When omitted, the status is surfaced
   * directly (no retry). This is the SAFETY NET; the common near-expiry case is
   * prevented up front by {@link ensureFreshToken}.
   */
  readonly onUnauthorized?: () => Promise<boolean>;
  /**
   * OPTIONAL hook invoked BEFORE every request to refresh the token PROACTIVELY
   * if it is near expiry — so the server never sees an expired token and the
   * player never eats the failed-401 round-trip + retry (the measured mid-play
   * stall). Wired in production to "if the id token is within ~60s of expiry,
   * await a refresh". It resolves when the token is as fresh as it can be made;
   * it never throws to the caller (a refresh failure just means the request goes
   * out with the current token and the reactive {@link onUnauthorized} 401 path
   * remains the backstop). When omitted, no proactive refresh happens (prior
   * behaviour — reactive-only).
   */
  readonly ensureFreshToken?: () => Promise<void>;
  /**
   * OPTIONAL telemetry breadcrumb sink. When provided, each request records a
   * coarse `METHOD /path → status` (or `→ network-error`) breadcrumb — the "what
   * did the app ask the server" half of the crash trail. BREADCRUMB ONLY: a
   * failed response is NOT itself reported as an error (an expected 400/401 is
   * not a bug), it is context recorded for a later real crash. Carries no
   * request/response bodies or auth headers — just method, path, and status.
   */
  readonly breadcrumbs?: { addBreadcrumb(kind: "api", message: string): void };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Map any thrown value to a player-safe message, NEVER a raw error or stack
 * trace (R13.5). An {@link ApiError} carries the server's own player-facing
 * `message` (the REST handlers return friendly text, not internals), so it is
 * safe to surface directly. Everything else — a network `TypeError` from
 * `fetch`, an unexpected throw, a non-Error value — collapses to a single
 * friendly fallback so a raw runtime message never reaches the UI.
 *
 * `fallback` lets a caller tailor the generic case to the action in progress
 * (e.g. "Failed to load campaigns.").
 */
export function friendlyErrorMessage(
  err: unknown,
  fallback = "Something went wrong. Please try again.",
): string {
  // The server's REST layer returns player-safe messages; an ApiError wraps one.
  if (err instanceof ApiError) return err.message;
  // Any other error (network failure, unexpected throw) must not leak its raw
  // message/stack — use the caller's friendly fallback.
  return fallback;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly getToken: TokenProvider;
  private readonly fetchImpl: typeof fetch;
  private readonly onUnauthorized?: () => Promise<boolean>;
  private readonly ensureFreshToken?: () => Promise<void>;
  private readonly breadcrumbs?: { addBreadcrumb(kind: "api", message: string): void };

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.getToken = options.getToken ?? (() => null);
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.onUnauthorized = options.onUnauthorized;
    this.ensureFreshToken = options.ensureFreshToken;
    this.breadcrumbs = options.breadcrumbs;
  }

  /** Issue one HTTP call with the current bearer token attached. */
  private async send(
    method: string,
    path: string,
    body: unknown,
  ): Promise<Response> {
    // Proactive pre-expiry refresh (the fix for the mid-play expiry stall): give
    // the auth layer a chance to renew a near-expiry token BEFORE we read it, so
    // the request carries a fresh bearer and the server never 401s on expiry.
    // A proactive refresh is BEST-EFFORT: any failure (including a throwing hook)
    // is swallowed here so the request still goes out with whatever token exists,
    // and the reactive 401 path below remains the backstop. We never let a
    // proactive-refresh problem turn a recoverable near-expiry into a hard
    // request failure (the guard makes that contract explicit, not implicit).
    if (this.ensureFreshToken) {
      try {
        await this.ensureFreshToken();
      } catch {
        // Fall through and send with the current token; the reactive 401
        // refresh+retry recovers if the token is actually stale.
      }
    }

    const headers: Record<string, string> = { Accept: "application/json" };
    const token = this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers["Content-Type"] = "application/json";

    try {
      const res = await this.fetchImpl(`${this.baseUrl}${path}`, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
      // Coarse telemetry breadcrumb: method + path + status. Context only —
      // never a reported error (an expected 4xx is not a bug). Best-effort.
      this.recordBreadcrumb(`${method} ${path} → ${res.status}`);
      return res;
    } catch (err) {
      // A network-level failure (fetch rejects) — record it as context before
      // it propagates. Still never a reported error on its own.
      this.recordBreadcrumb(`${method} ${path} → network-error`);
      throw err;
    }
  }

  /** Record an `api` breadcrumb if a sink is wired; never throws. */
  private recordBreadcrumb(message: string): void {
    try {
      this.breadcrumbs?.addBreadcrumb("api", message);
    } catch {
      // Breadcrumb recording must never affect the request path.
    }
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    let res = await this.send(method, path, body);

    // On an AUTH failure (expired/invalid token), attempt ONE refresh + retry so
    // a transparently-renewable session doesn't dead-end mid-use. BOTH 401 and
    // 403 are treated as refresh-eligible: a direct Lambda/authorizer 401 is one
    // signal, but the API Gateway COGNITO AUTHORIZER rejects an expired/invalid
    // token with 403 (not 401) — the request never reaches the handler — which is
    // exactly the mid-session-expiry turn failure observed in play. The retry is
    // capped at ONE, so a GENUINE authorization 403 (e.g. not a campaign member)
    // does not loop: the refreshed token hits the same 403 and it is surfaced.
    if ((res.status === 401 || res.status === 403) && this.onUnauthorized) {
      const refreshed = await this.onUnauthorized();
      if (refreshed) res = await this.send(method, path, body);
    }

    if (!res.ok) {
      let message = `HTTP ${res.status}`;
      try {
        // Two server error shapes: a plain `{ message }` and the validation
        // envelope `{ errors: [{ path, message }] }` (ApiErrorResponse). The
        // REST handlers return the latter on a 400 (e.g. create-campaign
        // rejecting an already-active character), so surface the field messages
        // — joined when there are several — instead of a bare "HTTP 400".
        const errBody = (await res.json()) as {
          message?: string;
          errors?: ReadonlyArray<{ message?: string }>;
        };
        if (Array.isArray(errBody?.errors) && errBody.errors.length > 0) {
          const joined = errBody.errors
            .map((e) => e?.message)
            .filter((m): m is string => typeof m === "string" && m.length > 0)
            .join("; ");
          if (joined.length > 0) message = joined;
        } else if (errBody?.message) {
          message = errBody.message;
        }
      } catch {
        // non-JSON error body; keep the status message.
      }
      throw new ApiError(res.status, message);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  // --- Characters (task 18.1) ---

  // The read routes below — GET /characters, GET /characters/{id}, and GET
  // /campaigns — are served by the ComputeStack REST API's read-side handler
  // (B1): a single READ-ONLY Lambda behind the Cognito authorizer that gates
  // every read on ownership (`getOwnedCharacter` → not_found for unknown OR
  // unowned) and derives campaign membership from the caller's roster (an owned
  // character's `currentCampaignId`, confirmed against the campaign's member
  // list). No separate memberships index is needed. See handlers/reads.ts.

  async listCharacters(): Promise<readonly CharacterLedger[]> {
    // Response is the shared ListCharactersResponse envelope `{ characters }`.
    const res = await this.request<ListCharactersResponse>("GET", "/characters");
    return res.characters;
  }

  async getCharacter(characterId: string): Promise<CharacterLedger> {
    // Response is the shared GetCharacterResponse envelope `{ character }`.
    const res = await this.request<GetCharacterResponse>(
      "GET",
      `/characters/${encodeURIComponent(characterId)}`,
    );
    return res.character;
  }

  async createCharacter(
    req: CreateCharacterWireRequest,
  ): Promise<CharacterLedger> {
    // Response is the shared CreateCharacterResponse envelope `{ character }`.
    const res = await this.request<CreateCharacterResponse>(
      "POST",
      "/characters",
      req,
    );
    return res.character;
  }

  // --- Campaigns (task 18.2 / 18.5) ---

  async listCampaigns(): Promise<readonly CampaignState[]> {
    // Response is the shared ListCampaignsResponse envelope `{ campaigns }`.
    const res = await this.request<ListCampaignsResponse>("GET", "/campaigns");
    return res.campaigns;
  }

  /**
   * Load the Play-screen journal history for a campaign (hydration-on-load): the
   * player-visible rolling summary plus the conversation lines since the summary
   * watermark, so a returning player is not dropped into a blank journal (the
   * live journal is otherwise WebSocket-push-only and a refresh starts it empty).
   * Member-gated server-side; a non-member or unknown campaign throws (404).
   */
  async getJournal(campaignId: string): Promise<GetJournalResponse> {
    return this.request<GetJournalResponse>(
      "GET",
      `/campaigns/${encodeURIComponent(campaignId)}/journal`,
    );
  }

  /**
   * Load the player-safe SPATIAL MAP for a campaign the caller is a member of
   * (Big Phase 1.5 UI). Returns the fog-of-war-filtered graph for one Z-slice
   * plus the "you are here" + map-library context. `z` omitted → the party's
   * current slice. Member-gated server-side (a non-member/unknown campaign
   * throws 404); the server strips every DM-only fact before responding.
   */
  async getSpatialMap(campaignId: string, z?: number): Promise<GetSpatialMapResponse> {
    const query = z !== undefined ? `?z=${encodeURIComponent(String(z))}` : "";
    return this.request<GetSpatialMapResponse>(
      "GET",
      `/campaigns/${encodeURIComponent(campaignId)}/map${query}`,
    );
  }

  async createCampaign(
    req: CreateCampaignWireRequest,
  ): Promise<CampaignState> {
    // Response is the shared CreateCampaignResponse envelope `{ campaign }`.
    const res = await this.request<CreateCampaignResponse>(
      "POST",
      "/campaigns",
      req,
    );
    return res.campaign;
  }

  /**
   * Bring a character into a campaign. Sends the shared BringInWireRequest
   * (`{ mode, campaignId, characterId }` for the select-existing path). The
   * server returns the attached character + seq tokens; callers act on
   * success-vs-throw.
   */
  bringIn(req: BringInWireRequest): Promise<BringInResponse> {
    return this.request(
      "POST",
      `/campaigns/${encodeURIComponent(req.campaignId)}/bring-in`,
      req,
    );
  }

  // --- Green Room (task 19) ---

  /**
   * Apply a Green Room transition (shared GreenRoomWireRequest; `adjust` carries
   * `ops`). The server returns `{ character, committedSeqToken }` on success (a
   * non-2xx throws an ApiError). Callers act on success-vs-throw.
   */
  greenRoom(req: GreenRoomWireRequest): Promise<GreenRoomResponse> {
    return this.request(
      "POST",
      `/campaigns/${encodeURIComponent(req.campaignId)}/green-room`,
      req,
    );
  }

  // --- Turn ingress / free-flow (R13.1, R31.2) ---

  /**
   * Submit a player action to the turn-ingress (`POST /campaigns/{id}/turns`).
   * Returns 202 (accepted or idempotent duplicate); the narrated result arrives
   * later over the WebSocket push channel. This is the SINGLE action-submission
   * entry — free-flow chat and explicit actions both flow through it (there is
   * no separate `/chat` route in Phase 1).
   */
  submitTurn(req: SubmitTurnWireRequest): Promise<SubmitTurnResponse> {
    return this.request(
      "POST",
      `/campaigns/${encodeURIComponent(req.campaignId)}/turns`,
      req,
    );
  }

  // --- Dice (task 20.2) ---

  /**
   * Roll dice (`POST /dice` — a top-level route; the campaign is carried in the
   * BODY, not the path, matching the ComputeStack wiring). The resolved roll is
   * returned AND appended to the campaign audit log (the AI's auto-report).
   */
  rollDice(req: DiceRollWireRequest): Promise<DiceRollResponse> {
    return this.request("POST", "/dice", req);
  }

  // --- Issue reporting (R-issues) ---

  /**
   * Report an issue from INSIDE a campaign (`POST /campaigns/{id}/issues`). The
   * server auto-anchors it to the campaign + the current conversation-log
   * position so a triager can later pull the exact transcript window — the
   * client sends only the free text. Returns the created issue.
   */
  async reportCampaignIssue(
    campaignId: string,
    req: CreateIssueWireRequest,
  ): Promise<CreateIssueResponse["issue"]> {
    const res = await this.request<CreateIssueResponse>(
      "POST",
      `/campaigns/${encodeURIComponent(campaignId)}/issues`,
      req,
    );
    return res.issue;
  }

  /**
   * Report a GENERAL issue not tied to a campaign (`POST /issues`) — e.g. a
   * login or navigation bug. No campaign anchor; the server records only the
   * reporter + the text. Returns the created issue.
   */
  async reportGeneralIssue(
    req: CreateIssueWireRequest,
  ): Promise<CreateIssueResponse["issue"]> {
    const res = await this.request<CreateIssueResponse>("POST", "/issues", req);
    return res.issue;
  }
}
